/**
 * Reads the `restricted-payment-methods` Flagsmith flag to resolve which payment
 * methods are hidden for the current Outlet's country.
 *
 * MoneyHash cannot scope payment methods per app or per region (its production
 * config enables Google Pay for every connected app), so regulatory bans — e.g.
 * the Saudi central bank blocking Google Pay — are enforced client-side via the
 * SDK's `restrictedMethods` prop.
 *
 * Flagsmith flag: `restricted-payment-methods` (JSON string)
 * Expected shape:
 *   { "SA": ["GOOGLE_PAY"] }
 *
 * Fail-open: a missing flag, malformed JSON, unknown country, or absent country
 * key all resolve to [] — the payment widget is never blocked by this lookup.
 */
import type { PaymentMethodType } from '@kitopi-com/payments';
import { useMemo } from 'react';
import { useCurrency } from '@/context/CurrencyContext/CurrencyContext';
import { useFeatureFlagValue } from './useFeatureFlag';

const KNOWN_METHODS: PaymentMethodType[] = ['CARD', 'APPLE_PAY', 'GOOGLE_PAY'];

function isPaymentMethodType(value: unknown): value is PaymentMethodType {
  return KNOWN_METHODS.includes(value as PaymentMethodType);
}

function parseRestrictionsMap(raw: unknown): Record<string, unknown> | null {
  if (typeof raw === 'string') {
    try {
      raw = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null;
  return raw as Record<string, unknown>;
}

export const useRestrictedPaymentMethods = (): PaymentMethodType[] => {
  const flagValue = useFeatureFlagValue('restricted-payment-methods');
  const { country } = useCurrency();

  return useMemo(() => {
    const map = parseRestrictionsMap(flagValue);
    const regionKey = country?.toUpperCase();
    const entry = map && regionKey ? map[regionKey] : null;

    if (!Array.isArray(entry)) {
      return [];
    }

    return entry.filter(isPaymentMethodType);
  }, [flagValue, country]);
};
